import { gql } from '@apollo/client';
import {
    PROJECT_LESS_FIELDS_FRAGMENT,
    PROJECT_USERS_FRAGMENT,
    PLANNING_PROJECT_LESS_FIELDS_FRAGMENT
} from './fragments/project';
import {
    PLANNING_WORKPACKAGE_FIELDS_FRAGMENT,
    WORKPACKAGE_FIELDS_FRAGMENT,
    WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT,
    WORKPACKAGE_USER_FIELDS_FRAGMENT
} from './fragments/workpackage';
import { PROJECT_ORGANIZATION_FIELDS_FRAGMENT } from './fragments/organizations';
import { BASIC_TASK_FIELDS_FRAGMENT, TASK_WITH_RESOURCED_FIELDS_FRAGMENT } from './fragments/task';
import { BASIC_LOCATION_FIELDS_FRAGMENT } from './fragments/location';
import { PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT } from '../../containers/ProjectsContainer/api/fragments/fragments';

export const GET_WORKPACKAGE_AUTOCOMPLETE = gql`
    query workPackageAutocomplete($Search: String, $From: Int, $To: Int, $WorkPackageIds: [Int!]) {
        workPackageRows(
            data: { Search: $Search, From: $From, To: $To, WorkPackageIds: $WorkPackageIds }
        ) {
            TotalRowCount
            WorkPackage {
                WorkPackageId
                WorkName
                WorkNbr
                Instructions
                Cables
                Pipes
                Lines
                LocationMoveAllowedAtObstacles
                MaxLocationMove
                AgreeLocationMoveWithDesigner
                StartDate
                EndDate
            }
        }
    }
`;

export const GET_WORKPACKAGES = gql`
    query workPackages {
        workPackages {
            ...WorkPackageFields
            Project {
                ...ProjectLessFields
            }
            WorkPackageSummary {
                ...WorkPackageSummaryFields
            }
        }
    }
    ${PROJECT_LESS_FIELDS_FRAGMENT}
    ${WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const GET_WORKPACKAGE_USERS = gql`
    query getWorkPackageUsersById($WorkPackageId: Int!) {
        workPackageById(data: { WorkPackageId: $WorkPackageId }) {
            ...WorkPackageUserFields
            Project {
                ...ProjectUsers
            }
        }
    }
    ${WORKPACKAGE_USER_FIELDS_FRAGMENT}
    ${PROJECT_USERS_FRAGMENT}
`;

export const GET_ORGANISATION_WORKPACKAGES = gql`
    query workPackagesByOrganizationId($OrganizationId: Int!) {
        workPackagesByOrganizationId(data: { OrganizationId: $OrganizationId }) {
            ...WorkPackageFields
            WorkPackageSummary {
                ...WorkPackageSummaryFields
            }
        }
    }
    ${WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const GET_WORKPACKAGE_ORGANIZATIONS = gql`
    query getWorkPackageOrganizationsById($WorkPackageId: Int!) {
        workPackageById(data: { WorkPackageId: $WorkPackageId }) {
            WorkPackageId
            WorkPackageOrganizations {
                ...ProjectOrganizationFields
            }
        }
    }
    ${PROJECT_ORGANIZATION_FIELDS_FRAGMENT}
`;

export const GET_WORKPACKAGE_EPIC = gql`
    query getWorkPackageEpicById($WorkPackageId: Int!) {
        workPackageById(data: { WorkPackageId: $WorkPackageId }) {
            WorkPackageId
            Epic {
                EpicId
                CoordinateSystemType {
                    CoordinateSystemTypeId
                    DatumCode
                    Name
                }
                HeightSystemType {
                    HeightSystemTypeId
                    Name
                }
            }
        }
    }
`;

export const GET_WORKPACKAGE_TASK = gql`
    query getWorkPackageTaskById($WorkPackageId: Int!) {
        workPackageById(data: { WorkPackageId: $WorkPackageId }) {
            WorkPackageId
            Tasks {
                ...TaskResourcedFields
            }
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_WORKPACKAGE_NOTRESOURCED_TASKS = gql`
    query getWorkPackageNotResourcedTasksById($WorkPackageId: Int!) {
        workPackageById(data: { WorkPackageId: $WorkPackageId }) {
            Tasks(data: { TaskAssignStatus: NOT_ASSIGNED }) {
                ...BasicTaskFields
                Location {
                    ...BasicLocationFields
                }
            }
        }
    }
    ${BASIC_TASK_FIELDS_FRAGMENT}
    ${BASIC_LOCATION_FIELDS_FRAGMENT}
`;

export const GET_WORKPACKAGESTATUS = gql`
    query WorkPackageStatus {
        workPackageStatuses {
            WorkPackageStatusId
            Name
        }
    }
`;

export const DELETE_WORKPACKAGE = gql`
    mutation deleteWorkPackage($WorkPackageId: Int!) {
        deleteWorkPackage(data: { WorkPackageId: $WorkPackageId })
    }
`;

export const GET_WORKPACKAGES_WITH_TASKS = gql`
    query workPackagesWithTasks {
        workPackages {
            ...WorkPackageFields
            Resourced: Tasks(data: { TaskAssignStatus: ASSIGNED }) {
                ...BasicTaskFields
            }
            NotResourced: Tasks(data: { TaskAssignStatus: NOT_ASSIGNED }) {
                ...BasicTaskFields
            }
        }
    }
    ${BASIC_TASK_FIELDS_FRAGMENT}
`;

export const ADD_TASK_TO_WORKPACKAGE = gql`
    mutation addPlanningTaskToProject($WorkPackageId: Int!, $TaskIds: [Int!], $UserId: Int!) {
        addTaskToProject(
            data: { WorkPackageId: $WorkPackageId, TaskIds: $TaskIds, UserId: $UserId }
        ) {
            ProjectId
            Tasks {
                ...BasicTaskFields
                Location {
                    ...BasicLocationFields
                }
            }
        }
    }
    ${BASIC_TASK_FIELDS_FRAGMENT}
    ${BASIC_LOCATION_FIELDS_FRAGMENT}
`;

export const REMOVE_TASK_FROM_PROJECT = gql`
    mutation removeTaskFromProject($ProjectId: Int!, $TaskIds: [Int!]) {
        removeTaskFromProject(data: { ProjectId: $ProjectId, TaskIds: $TaskIds }) {
            ProjectId
            Tasks {
                TaskId
            }
            WorkPackage {
                WorkPackageId
                WorkPackageStatus {
                    WorkPackageStatusId
                    Name
                }
            }
        }
    }
`;

export const CREATE_WORKPACKAGE = gql`
    mutation createWorkPackage(
        $WorkName: String!
        $WorkNbr: String!
        $EpicId: Int
        $EpicName: String
        $EpicNbr: String
        $CoordinateSystemTypeId: Int
        $HeightSystemTypeId: Int
        $Target: String
        $StartDate: Date
        $EndDate: Date
        $Locations: [CreateLocationInputType!]
        $DataOwner: String
        $Secret: Boolean
        $Cables: Boolean
        $Pipes: Boolean
        $Lines: Boolean
        $LocationMoveAllowedAtObstacles: Boolean
        $MaxLocationMove: Float
        $AgreeLocationMoveWithDesigner: Boolean
        $DataSource: WorkPackageDataSourceInputType
    ) {
        createWorkPackage(
            data: {
                WorkName: $WorkName
                WorkNbr: $WorkNbr
                EpicId: $EpicId
                EpicName: $EpicName
                EpicNbr: $EpicNbr
                CoordinateSystemTypeId: $CoordinateSystemTypeId
                HeightSystemTypeId: $HeightSystemTypeId
                Target: $Target
                StartDate: $StartDate
                EndDate: $EndDate
                Locations: $Locations
                EpicDataOwner: $DataOwner
                EpicSecret: $Secret
                Cables: $Cables
                Pipes: $Pipes
                Lines: $Lines
                LocationMoveAllowedAtObstacles: $LocationMoveAllowedAtObstacles
                MaxLocationMove: $MaxLocationMove
                AgreeLocationMoveWithDesigner: $AgreeLocationMoveWithDesigner
                DataSource: $DataSource
            }
        ) {
            ...WorkPackageFields
            WorkPackageSummary {
                ...WorkPackageSummaryFields
            }
            WorkPackageUsers {
                __typename
                User {
                    Firstname
                    Lastname
                    UserId
                    UserState
                    Phone
                    Email
                    OwnOrganization {
                        OrganizationId
                        Name
                    }
                }
            }
        }
    }
    ${WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const UPDATE_WORKPACKAGE_STATUS = gql`
    mutation updateWorkPackageStatus($WorkPackageId: Int!, $WorkPackageStatusId: Int) {
        updateWorkPackage(data: { WorkPackageId: $WorkPackageId, StatusId: $WorkPackageStatusId }) {
            ...ProjectsContainerWorkPackages
        }
    }
    ${PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT}
`;

export const UPDATE_WORKPACKAGE_PLANNING_CARD = gql`
    mutation updateWorkPackagePlanningCard(
        $WorkPackageId: Int!
        $WorkName: String
        $WorkNbr: String
    ) {
        updateWorkPackage(
            data: { WorkPackageId: $WorkPackageId, WorkName: $WorkName, WorkNbr: $WorkNbr }
        ) {
            ...PlanningWorkPackageFields
            WorkPackageSummary {
                ...WorkPackageSummaryFields
            }
        }
    }
    ${PLANNING_WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const UPDATE_WORKPACKAGE = gql`
    mutation updateWorkPackage(
        $WorkPackageId: Int!
        $WorkName: String
        $WorkNbr: String
        $EpicId: Int
        $Target: String
        $StartDate: Date
        $EndDate: Date
        $WorkPackageStatusId: Int
        $Instructions: String
        $Cables: Boolean
        $Pipes: Boolean
        $Lines: Boolean
        $LocationMoveAllowedAtObstacles: Boolean
        $MaxLocationMove: Float
        $AgreeLocationMoveWithDesigner: Boolean
        $AddWorkPackageUsers: [UpdateWorkPackageAddUsersInputType!]
        $RemoveWorkPackageUsers: [UpdateWorkPackageRemoveUsersInputType!]
        $AddTaskTypeInstruction: [UpdateWorkpackageAddTaskInstruction!]
        $RemoveTaskTypeInstruction: [UpdateWorkPackageRemoveTaskInstruction!]
        $DataSource: WorkPackageDataSourceInputType
    ) {
        updateWorkPackage(
            data: {
                WorkPackageId: $WorkPackageId
                WorkName: $WorkName
                WorkNbr: $WorkNbr
                EpicId: $EpicId
                Target: $Target
                StartDate: $StartDate
                EndDate: $EndDate
                StatusId: $WorkPackageStatusId
                Cables: $Cables
                Pipes: $Pipes
                Lines: $Lines
                Instructions: $Instructions
                LocationMoveAllowedAtObstacles: $LocationMoveAllowedAtObstacles
                MaxLocationMove: $MaxLocationMove
                AgreeLocationMoveWithDesigner: $AgreeLocationMoveWithDesigner
                AddWorkPackageUsers: $AddWorkPackageUsers
                RemoveWorkPackageUsers: $RemoveWorkPackageUsers
                AddTaskTypeInstruction: $AddTaskTypeInstruction
                RemoveTaskTypeInstruction: $RemoveTaskTypeInstruction
                DataSource: $DataSource
            }
        ) {
            ...WorkPackageFields
            WorkPackageSummary {
                ...WorkPackageSummaryFields
            }
        }
    }
    ${WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const CREATE_LOCATIONS_TO_WORKPACKAGE = gql`
    mutation createLocationsToWorkPackage(
        $WorkPackageId: Int!
        $Locations: [CreateLocationInputType!]
    ) {
        createLocationsToWorkPackage(data: { WorkPackageId: $WorkPackageId, Locations: $Locations })
    }
`;

export const GET_USER_WORKPACKAGETROLES = gql`
    query userWorkPackageRoleTypes {
        userWorkPackageRoleTypes {
            UserWorkPackageRoleTypeId
            Name
        }
    }
`;

export const ADD_USER_WORKPACKAGETROLE = gql`
    mutation addUserWorkPackageRole(
        $WorkPackageId: Int!
        $UserId: Int!
        $UserWorkPackageRoleTypeId: Int!
    ) {
        addUserWorkPackageRole(
            data: {
                WorkPackageId: $WorkPackageId
                UserId: $UserId
                UserWorkPackageRoleTypeId: $UserWorkPackageRoleTypeId
            }
        ) {
            ...ProjectsContainerWorkPackages
        }
    }
    ${PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT}
`;

export const REMOVE_USER_WORKPACKAGETROLE = gql`
    mutation removeUserWorkPackageRole(
        $WorkPackageId: Int!
        $UserId: Int!
        $UserWorkPackageRoleTypeId: Int!
    ) {
        removeUserWorkPackageRole(
            data: {
                WorkPackageId: $WorkPackageId
                UserId: $UserId
                UserWorkPackageRoleTypeId: $UserWorkPackageRoleTypeId
            }
        ) {
            ...ProjectsContainerWorkPackages
        }
    }
    ${PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT}
`;

export const ADD_USER_WORKPACKAGETROLE_TO_ORGANIZATION = gql`
    mutation addUserWorkPackageRoleOwnOrganization($WorkPackageId: Int!) {
        addUserWorkPackageRoleOwnOrganization(data: { WorkPackageId: $WorkPackageId }) {
            ...ProjectsContainerWorkPackages
        }
    }
    ${PROJECTS_CONTAINER_WORKPACKAGES_FRAGMENT}
`;

export const GET_GEOSUITE_DATA = gql`
    query getGeosuiteData($WorkPackageDataSourceId: Int!) {
        getGeosuiteData(data: { WorkPackageDataSourceId: $WorkPackageDataSourceId }) {
            Name
            Methods
            x
            y
        }
    }
`;
